import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number | null | undefined): string {
  // Handle null, undefined, or invalid number cases
  if (num == null || typeof num !== "number" || isNaN(num)) {
    return "0";
  }

  // Simply use toLocaleString with thousands separators for all numbers
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function truncateApiKey(
  key: string | null | undefined,
  visibleChars: number = 8
): string {
  // Handle null, undefined, or empty string cases
  if (!key || typeof key !== "string") {
    return "Invalid API Key";
  }

  // If key is shorter than or equal to twice the visible chars, return as is
  if (key.length <= visibleChars * 2) {
    return key;
  }

  // Truncate and show first and last parts
  return `${key.slice(0, visibleChars)}...${key.slice(-visibleChars)}`;
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatCurrency(
  amount: number,
  currency: string = "usd",
  inCents: boolean = true
): string {
  const actualAmount = inCents ? amount / 100 : amount;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: actualAmount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 4,
    }).format(actualAmount);
  } catch (error) {
    // Fallback for unsupported currencies
    return `$${actualAmount}`;
  }
}

// Legacy function for backward compatibility
export function formatCurrencyLegacy(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "SGD",
  }).format(amount / 100); // Assuming amount is in cents
}

export function generateApiKeyName(): string {
  const adjectives = [
    "Quick",
    "Smart",
    "Fast",
    "Secure",
    "Reliable",
    "Efficient",
  ];
  const nouns = ["Key", "Token", "Access", "Gateway", "Bridge", "Portal"];

  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 1000);

  return `${adjective} ${noun} ${number}`;
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

export function getRelativeTime(date: string | Date): string {
  const now = new Date();
  const target = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return formatDate(date);
}

// Additional utility functions for better type safety
export function safeParseInt(value: any, fallback: number = 0): number {
  if (typeof value === "number") return Math.floor(value);
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

export function safeParseFloat(value: any, fallback: number = 0): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

export function isValidString(value: any): value is string {
  return typeof value === "string" && value.length > 0;
}

export function isValidNumber(value: any): value is number {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

export const formatInterval = (interval: string): string => {
  switch (interval) {
    case "month":
      return "monthly";
    case "year":
      return "yearly";
    default:
      return interval;
  }
};
